import React, { useState } from 'react';
import { ShieldCheck, ScrollText } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

/**
 * One-time consent dialog for interpreters: Berufskodex (Code of Conduct).
 * Shown the first time an interpreter logs in until they accept.
 * Blocks the screen — no close button. Acceptance is sent to backend.
 */
const BerufskodexModal = ({ open, onAccepted }) => {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!checked) return;
    setSubmitting(true);
    try {
      await api.post('/interpreters/me/consent', { accepted: true });
      toast.success('Vielen Dank — der Berufskodex wurde bestätigt.');
      onAccepted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="berufskodex-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 bg-[#0F4C81]/5 flex items-center gap-3">
          <ShieldCheck className="w-6 h-6 text-[#0F4C81]" />
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">
              Berufskodex für Dolmetscher
            </h2>
            <p className="text-xs text-slate-600">
              Bitte lesen und einmalig bestätigen, bevor Sie Aufträge annehmen können.
            </p>
          </div>
        </div>

        <div className="p-6 overflow-y-auto text-sm text-slate-800 leading-relaxed space-y-4">
          <div className="flex items-center gap-2 text-[#0F4C81] font-semibold">
            <ScrollText className="w-4 h-4" />
            <span>Berufskodex – DolmetschDirekt</span>
          </div>

          <Section title="§1 Geltungsbereich">
            Dieser Berufskodex gilt für alle Dolmetscherinnen und Dolmetscher, die über die
            Plattform „DolmetschDirekt" tätig werden. Mit Annahme eines Auftrags verpflichten
            sich die Dolmetscher zur Einhaltung der nachfolgenden Grundsätze.
          </Section>

          <Section title="§2 Fachliche Sorgfalt und Kompetenz">
            Dolmetscher führen ihre Aufträge nach bestem Wissen und Gewissen aus. Aufträge
            dürfen ausschließlich angenommen werden, wenn sie im Einklang mit den eigenen
            fachlichen Kompetenzen, Qualifikationen und Erfahrungen stehen.
          </Section>

          <Section title="§3 Verschwiegenheitspflicht">
            Dolmetscher unterliegen einer strikten Verschwiegenheitspflicht, die auch über
            die Beendigung der Zusammenarbeit hinaus fortbesteht. Alle im Rahmen eines
            Auftrags erlangten Informationen sind streng vertraulich zu behandeln und dürfen
            weder für eigene Zwecke noch zum Vorteil Dritter verwendet oder weitergegeben werden.
          </Section>

          <Section title="§4 Unparteilichkeit und Neutralität">
            Dolmetscher sind zur Wahrung absoluter Unparteilichkeit verpflichtet. Sie
            verpflichten sich zu einem respektvollen und diskriminierungsfreien Umgang mit
            allen am Auftrag beteiligten Parteien – unabhängig von Geschlecht, Herkunft,
            Religion, politischer Überzeugung, sexueller Orientierung, Alter oder körperlichen
            bzw. seelischen Beeinträchtigungen. Persönliche Meinungen oder Bewertungen dürfen
            während der Dolmetschleistung nicht geäußert werden.
          </Section>

          <Section title="§5 Rolle als Sprach- und Kulturmittler">
            Dolmetscher handeln ausschließlich im Rahmen ihrer Funktion als Sprach- und
            Kulturmittler. Jegliches Handeln außerhalb dieser Rolle ist transparent zu machen.
            Ein Verlassen der rein translatorischen Tätigkeit ist klar zu kommunizieren, um
            Missverständnisse zu vermeiden.
          </Section>

          <Section title="§6 Qualitätssicherung und Fortbildung">
            Dolmetscher sind verpflichtet, ihre sprachlichen und fachlichen Kompetenzen
            eigenständig aufrechtzuerhalten und kontinuierlich weiterzuentwickeln. Die Teilnahme
            an einschlägigen Fortbildungsmaßnahmen wird ausdrücklich empfohlen.
          </Section>

          <Section title="§7 Grundsätze der Dolmetschleistung">
            Die Dolmetschleistung erfolgt grundsätzlich in der ersten Person Singular. Der zu
            übertragende Inhalt ist vollständig, korrekt und sinngemäß in die Zielsprache zu
            übertragen. Es ist unzulässig, Inhalte eigenmächtig hinzuzufügen, wegzulassen oder
            inhaltlich zu verändern. Erkannte Fehler sind unverzüglich zu korrigieren. Bei
            Unklarheiten sind Dolmetscher berechtigt, Rückfragen zu stellen, Wiederholungen
            einzufordern oder Erläuterungen zu verlangen.
          </Section>

          <Section title="§8 Verbot von Aufzeichnungen">
            Jegliche Form der Aufzeichnung, Speicherung, Weitergabe oder Weiterverwertung von
            Gesprächsinhalten ist strengstens untersagt. Dies gilt insbesondere für Tonaufnahmen,
            Videoaufnahmen, Screenshots sowie digitale oder analoge Mitschnitte jeglicher Art.
            Verstöße können rechtliche Konsequenzen nach sich ziehen.
          </Section>

          <Section title="§9 Auftreten und Arbeitsumfeld">
            Dolmetscher sind verpflichtet, ein professionelles und dem Berufsstand angemessenes
            Auftreten zu gewährleisten. Die Leistung ist in einem geeigneten, ruhigen und
            ungestörten Umfeld zu erbringen. Es ist sicherzustellen, dass keine unbefugten
            Dritten Zugang zu den Gesprächsinhalten erhalten.
          </Section>

          <Section title="§10 Zustimmung zum Berufskodex">
            Die Einhaltung dieses Berufskodex ist Voraussetzung für die Tätigkeit über die
            Plattform DolmetschDirekt. Mit der Registrierung und Annahme von Aufträgen
            bestätigen Dolmetscher, diesen Kodex gelesen, verstanden und akzeptiert zu haben.
          </Section>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              data-testid="berufskodex-accept-checkbox"
              className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#0F4C81] focus:ring-[#0F4C81]"
            />
            <span className="text-sm text-slate-800">
              Ich habe den Berufskodex gelesen und akzeptiere die genannten Bedingungen.
            </span>
          </label>
          <button
            onClick={submit}
            disabled={!checked || submitting}
            data-testid="berufskodex-submit-btn"
            className="w-full bg-[#0F4C81] hover:bg-[#0A365C] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-md px-4 py-3 text-sm transition-colors inline-flex items-center justify-center gap-2"
          >
            <ShieldCheck className="w-4 h-4" />
            Zustimmen & weiter
          </button>
          <p className="text-xs text-slate-500 text-center">
            Ohne Zustimmung können Sie keine Aufträge annehmen.
          </p>
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h3 className="font-semibold text-slate-900 text-sm mb-1">{title}</h3>
    <p className="text-slate-700 text-sm leading-relaxed">{children}</p>
  </div>
);

export default BerufskodexModal;
